import {
  Badge,
  Grid,
  keyframes,
  Snackbar,
  styled,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ChatBot, { ChatBotProvider, useChatWindow } from 'react-chatbotify';
import { useTranslation } from 'react-i18next';
import chappiLogo from '../../../assets/image/chappi/chappi.png';
import { getErrorCodeFromError, GraphQlRequestErrorCode } from '../api/utils';
import { ChartComponent } from '../v2/common/components/charts/ChartComponent';
import {
  BarChartComponentConfig,
  KpiV2ComponentConfig,
  LineChartComponentConfig,
  OvertimeTableComponentConfig,
  PieChartComponentConfig,
} from '../v2/common/components/dashboards/types';
import {
  useChappiServiceContext,
  useDomainScopedErrorTrackerContext,
} from '../v2/context/contexts';
import {
  ChappiResponse,
  parseVisualization,
  VisualizationBackend,
} from './chappi_types';

// --- Define Keyframes for Glow Effect (Stronger, Longer Double Pulse) ---
const glowEffect = keyframes`
  0%   { box-shadow: 0 0 0px 0px rgba(0, 165, 153, 0); }      /* Start invisible */
  15%  { box-shadow: 0 0 16px 6px rgba(0, 165, 153, 0.8); } /* 1st pulse peak (Bigger, Darker) */
  40%  { box-shadow: 0 0 4px 1px rgba(0, 165, 153, 0.2); }  /* 1st pulse end (Slightly darker fade) */
  50%  { box-shadow: 0 0 0px 0px rgba(0, 165, 153, 0); }      /* Pause */
  65%  { box-shadow: 0 0 16px 6px rgba(0, 165, 153, 0.8); } /* 2nd pulse peak (Bigger, Darker) */
  90%  { box-shadow: 0 0 4px 1px rgba(0, 165, 153, 0.2); }  /* 2nd pulse end (Slightly darker fade) */
  100% { box-shadow: 0 0 0px 0px rgba(0, 165, 153, 0); }     /* End invisible */
`;

const VisualizationDiv = styled(Grid)`
  position: absolute;
  background-color: white;
  border-top: 2em solid #007565;
  border-top-left-radius: 1em;
  border-top-right-radius: 1em;
  box-shadow: 3px 3px 10px rgba(0, 0, 0, 0.1);
  transition: width 1s ease-in-out;
  overflow-y: auto;
  gap: 2em;
  padding: 1em;

  /* Styling the scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #007565;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #00a599;
  }
`;

const VisualizationItem = styled('div')`
  padding: 0.2em;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 0.3em;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }

  /* Add subtle shadow and rounded corners */
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  height: 40vh;
  width: 55vw;
  /* Remove background transition */

  &.highlight-viz {
    /* Apply stronger double pulse glow effect animation */
    animation: ${glowEffect} 3s ease-in-out forwards; /* Reverted duration to 3s */
  }
`;

// Define an interface for the params object based on documentation
interface ChatbotFlowParams {
  userInput: string;
  injectMessage: (
    content: string | JSX.Element,
    sender?: 'user' | 'bot'
  ) => Promise<string | null>;
  // Add other params from docs if needed, e.g.:
  // streamMessage: (content: string | JSX.Element, sender?: 'user' | 'bot') => Promise<void>;
  // currPath: string | null;
  // prevPath: string | null;
  // goToPath: (pathToGo: string) => Promise<void>;
}

// Define the type for the state entry
interface VisualizationEntry {
  config:
    | LineChartComponentConfig
    | PieChartComponentConfig
    | OvertimeTableComponentConfig
    | KpiV2ComponentConfig
    | BarChartComponentConfig;
  repetitionCount: number;
}

export const ChappiChatbot = () => {
  return (
    <ChatBotProvider>
      <ChappiChatbotInner />
    </ChatBotProvider>
  );
};

const ChappiChatbotInner = () => {
  const chappiService = useChappiServiceContext();
  const errorTracker = useDomainScopedErrorTrackerContext();
  const { t } = useTranslation();
  const { isChatWindowOpen } = useChatWindow();
  const [form, setForm] = useState({});
  const [showVisualization, setShowVisualization] = useState(false);
  const [visualizationData, setVisualizationData] = useState<
    VisualizationEntry[]
  >([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const visualizationRef = React.useRef<HTMLDivElement>(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);

  // Function to copy conversation link to clipboard
  const copyConversationLink = useCallback(() => {
    if (!conversationId) return;

    const url = `${window.location.origin}/panalyt-superadmin/chappi-conversations?conversationId=${conversationId}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopySnackbarOpen(true);
      })
      .catch((err) => {
        console.error('Failed to copy conversation link:', err);
        errorTracker.trackError({
          error: new Error('Failed to copy conversation link'),
          contexts: { error: err, conversationId },
          tags: { 'panalyt.component': 'Chappi' },
        });
      });
  }, [conversationId]);

  // Handle snackbar close
  const handleSnackbarClose = useCallback(() => {
    setCopySnackbarOpen(false);
  }, []);

  // Function to scroll to bottom of visualization div
  const scrollToBottom = () => {
    const ref = visualizationRef.current;
    if (ref) {
      setTimeout(() => {
        if (ref) {
          // Double-check ref is still valid after timeout
          ref.scrollTo({
            top: ref.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 350);
    }
  };

  // Effect to scroll to bottom when new visualization is added
  React.useEffect(() => {
    if (showVisualization && visualizationData.length > 0) {
      scrollToBottom();
    }
  }, [visualizationData.length, showVisualization]);

  // --- Effect to Reset Highlight ---
  useEffect(() => {
    if (highlightedIndex !== null) {
      const timer = setTimeout(() => {
        setHighlightedIndex(null);
      }, 3000); // Reverted duration to 3000ms
      return () => clearTimeout(timer);
    }
  }, [highlightedIndex]);

  const handleChappiResponse = (
    response: ChappiResponse[],
    params: ChatbotFlowParams
  ) => {
    try {
      if (!response || !Array.isArray(response) || response.length === 0) {
        console.warn('Empty or invalid Chappi response received:', response);
        errorTracker.trackError({
          error: new Error('Empty or invalid Chappi response received'),
          contexts: { conversationId, response },
          tags: { 'panalyt.component': 'Chappi' },
        });
        params.injectMessage(t('pages:chappi.chat.error.general'));
        return;
      }

      // Process each response item
      response.forEach((item) => {
        if (!item || !item.__typename) {
          console.error('Invalid item in Chappi response array:', item);
          errorTracker.trackError({
            error: new Error('Invalid item in Chappi response array'),
            contexts: { conversationId },
            tags: { 'panalyt.component': 'Chappi' },
          });
          params.injectMessage(t('pages:chappi.chat.error.general'));
          return;
        }

        switch (item.__typename) {
          // --- Visualization Types ---
          case 'ChappiKpiVisualization':
          case 'ChappiLineChartVisualization':
          case 'ChappiOverTimeBarChartVisualization':
          case 'ChappiPieChartVisualization':
          case 'ChappiTableVisualization':
          case 'ChappiTimePeriodBarChartVisualization':
            try {
              const chappiViz = item as VisualizationBackend;
              const newVizConfig = parseVisualization(chappiViz);
              console.debug('chappi chart component config:', newVizConfig);

              setVisualizationData((prevData) => {
                const lastEntry =
                  prevData.length > 0 ? prevData[prevData.length - 1] : null;
                // Compare with the last entry's config
                if (
                  lastEntry &&
                  JSON.stringify(newVizConfig) ===
                    JSON.stringify(lastEntry.config)
                ) {
                  console.debug(
                    'Duplicate visualization detected, incrementing count.'
                  );
                  // Update repetition count of the last entry
                  setHighlightedIndex(prevData.length - 1);
                  return prevData.map((entry, index) =>
                    index === prevData.length - 1
                      ? { ...entry, repetitionCount: entry.repetitionCount + 1 }
                      : entry
                  );
                } else {
                  console.debug(
                    'New or different visualization, adding to list.'
                  );
                  // Add new entry
                  setHighlightedIndex(prevData.length);
                  return [
                    ...prevData,
                    { config: newVizConfig, repetitionCount: 1 },
                  ];
                }
              });

              setShowVisualization(true);
              if (item.responseForUser) {
                params.injectMessage(item.responseForUser);
              }
            } catch (e) {
              console.error(
                'Error parsing or processing visualization response:',
                e,
                item
              );
              errorTracker.trackError({
                error: new Error('Error processing visualization response'),
                contexts: { conversationId, error: e },
                tags: { 'panalyt.component': 'Chappi' },
              });
              // Inject an error message for this specific failure
              params.injectMessage(
                t('pages:chappi.chat.error.visualizationError')
              );
            }
            break;

          // --- Simple Message Types --- Use injectMessage
          case 'ChappiCategorizationResponse':
            console.debug(
              `Received ChappiCategorizationResponse ${item.questionCategory}, ignoring for display`
            );
            break;
          case 'ChappiCauseInvestigationResponse':
          case 'ChappiDataDiscrepancyResponse':
          case 'ChappiExplainPreviousChartAnalyticsResponse':
          case 'ChappiExplainPreviousSingleNumberAnalyticsResponse':
          case 'ChappiExplanationOfCalculationOrDefinitionResponse':
          case 'ChappiFindingSpecificEmployeesResponse':
          case 'ChappiHowToUseChappiResponse':
          case 'ChappiHowToUseThePanalytAppResponse':
          case 'ChappiOtherOrUnknownResponse':
          case 'ChappiPanalytAppGeneralQuestionOrProblemResponse':
          case 'ChappiPanalytContractResponse':
          case 'ChappiPredictiveAnalyticsForecastingResponse':
            params.injectMessage(item.message);
            break;

          case 'ChappiProblemEncounteredResponse': // Structured errors from backend
            console.error('Chappi encountered a problem:', item.message);
            errorTracker.trackError({
              error: new Error(
                `ChappiProblemEncounteredResponse: ${item.message}`
              ),
              contexts: { conversationId, responseItem: item },
              tags: { 'panalyt.component': 'Chappi' },
            });
            params.injectMessage(item.message); // Still show the message to the user
            break;

          case 'ChappiRecommendationsSuggestionsResponse':
          case 'ChappiSingleNumberAnalyticsResponse':
            params.injectMessage(item.message);
            break;

          // --- Other Types ---
          case 'ChappiVisualizationTypeResponse':
            // Purely informational for the backend's process.
            console.debug(
              'Received ChappiVisualizationTypeResponse:',
              item.visualizationType
            );
            break;

          default:
            console.warn(
              'Unhandled Chappi response type:',
              item.__typename,
              item
            );
            errorTracker.trackError({
              error: new Error('Unhandled Chappi response type'),
              contexts: { conversationId, type: item.__typename },
              tags: { 'panalyt.component': 'Chappi' },
            });
            params.injectMessage(
              t('pages:chappi.chat.error.unknownResponseType')
            );
            break;
        }
      });

      // Return nothing, as messages/visualizations are handled via injectMessage or state
      return; // Important: Prevents chatbot from showing 'undefined'
    } catch (error) {
      console.error('Error handling Chappi response:', error);
      errorTracker.trackError({
        error: new Error('Error handling Chappi response'),
        contexts: { conversationId, error },
        tags: { 'panalyt.component': 'Chappi' },
      });
      // Don't return the error directly, inject it if params are available
      if (params && params.injectMessage) {
        params.injectMessage(t('pages:chappi.chat.error.unavailable'));
      } else {
        // Fallback if params aren't available during the error
        return t('pages:chappi.chat.error.unavailable');
      }
      return; // Prevent default return
    }
  };

  const flow = {
    start: {
      message: t('pages:chappi.chat.welcome.introduction'),
      path: 'ask_question',
    },
    ask_question: {
      // Use the specific interface here as well
      message: async (params: ChatbotFlowParams) => {
        try {
          // If this is the first question (no conversation ID), start a new conversation
          if (!conversationId) {
            const startResult = await chappiService.startConversation(
              params.userInput
            );

            if (startResult.startConversation) {
              setConversationId(startResult.startConversation.conversation.id);
              return handleChappiResponse(
                startResult.startConversation.firstResponse as ChappiResponse[],
                params
              );
            }
          } else {
            // For subsequent questions, use the existing conversation ID
            const result = await chappiService.askChappi(
              params.userInput,
              conversationId
            );

            if (result.askChappi) {
              return handleChappiResponse(
                result.askChappi as ChappiResponse[],
                params
              );
            }
          }
        } catch (error) {
          const errorCode = getErrorCodeFromError(error);
          if (errorCode === GraphQlRequestErrorCode.Err_RateLimitExceeded) {
            console.warn('Rate limit exceeded in Chappi conversation:', error);
            errorTracker.trackError({
              error: new Error('Rate limit exceeded in Chappi conversation'),
              contexts: { conversationId, errorCode },
              tags: { 'panalyt.component': 'Chappi' },
            });
            return t('pages:chappi.chat.error.rateLimitExceeded');
          } else {
            console.error('Unexpected error in Chappi conversation', error);
            errorTracker.trackError({
              error: new Error(`Unexpected error in Chappi conversation`),
              contexts: { conversationId, error },
              tags: { 'panalyt.component': 'Chappi' },
            });
            return t('pages:chappi.chat.error.unavailable');
          }
        }
      },
      // Also update the function attribute if it uses params
      function: (params: ChatbotFlowParams) =>
        setForm({ ...form, age: params.userInput }),
      path: 'ask_question',
    },
  };
  return (
    <>
      <ChatBot
        flow={flow}
        settings={{
          general: { primaryColor: '#00a599', secondaryColor: '#007565' },
          notification: { disabled: true },
          header: {
            title: (
              <b style={{ fontSize: '20px' }}>
                {t('pages:chappi.chat.ui.headerTitle')}
              </b>
            ),
            avatar: chappiLogo,
          },
          chatButton: { icon: chappiLogo },
          footer: { text: '', buttons: [] },
          tooltip: { mode: 'NEVER' },
          chatHistory: {
            disabled: true,
          },
          chatInput: {
            enabledPlaceholderText: t('pages:chappi.chat.ui.inputPlaceholder'),
          },
        }}
      />
      {isChatWindowOpen && showVisualization && (
        <VisualizationDiv
          ref={visualizationRef}
          sx={{
            bottom: '20px',
            right: '405px',
            width: '60vw',
            height: '550px',
            opacity: 1,
          }}
          container
          direction="column"
          alignItems="center"
          flexWrap="nowrap"
        >
          {/* Copy Link top bar that stretches across the full width */}
          {conversationId && (
            <div
              onClick={copyConversationLink}
              title="Copy link to this conversation"
              style={{
                width: 'calc(100% + 16px)',
                marginLeft: '-8px',
                marginTop: '-8px',
                marginBottom: '8px',
                padding: '0 8px',
                height: '18px',
                backgroundColor: 'rgba(0, 117, 101, 0.1)',
                borderBottom: '1px solid rgba(0, 117, 101, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '10px',
                color: '#007565',
                fontWeight: 500,
                lineHeight: 1,
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#007565"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: '4px' }}
              >
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
              </svg>
              {t('pages:chappi.chat.ui.copyLink')}
            </div>
          )}

          {visualizationData.map(({ config, repetitionCount }, index) => {
            const itemClassName =
              index === highlightedIndex ? 'highlight-viz' : '';
            return (
              <Grid item key={index} sx={{ position: 'relative' }}>
                <VisualizationItem key={index} className={itemClassName}>
                  <ChartComponent
                    pageOptions={{ timeSlider: { enabled: false } }}
                    componentConfig={config}
                  />
                  {repetitionCount > 1 && (
                    <Tooltip
                      title={t('pages:chappi.chat.ui.repeatedVisualization', {
                        count: repetitionCount,
                      })}
                      arrow
                    >
                      <Badge
                        badgeContent={repetitionCount}
                        color="primary"
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          '& .MuiBadge-badge': {
                            fontSize: '0.7rem',
                            height: '18px',
                            minWidth: '18px',
                          },
                        }}
                      />
                    </Tooltip>
                  )}
                </VisualizationItem>
              </Grid>
            );
          })}
        </VisualizationDiv>
      )}

      {/* Snackbar for copy notifications */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={t('pages:chappi.chat.ui.linkCopied')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};
